import React, { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Download, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    csvSection, downloadCSV, fileDate, metaLines, nodeToPngDataUrl, slug,
} from './exportUtils';

/**
 * Page-level export for the Circularity dashboard (copy of the Operations
 * DashboardExport, adapted).
 *
 * Every card that can be exported renders its root element with:
 *   className="exportable-section"
 *   data-export-title="Chart title"
 *   data-export-data={JSON.stringify({ title, headers, rows })}
 *
 * Export as CSV       -> one file: a header (period, bins, export time in the
 *                        viewer's timezone) followed by one section per chart.
 * Export as PNG (ZIP) -> one PNG per chart + the combined CSV.
 *
 * `meta` = { period, bins, wasteType } for the header, built by the dashboard.
 */
export function CircularityExport({ children, meta }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const [exportStatus, setExportStatus] = useState(null); // null | 'preparing' | 'ready' | 'error'
    const [exportProgress, setExportProgress] = useState(0);
    const [exportType, setExportType] = useState(null);
    const [download, setDownload] = useState(null); // { blob, name }
    const cancelled = useRef(false);

    useEffect(() => {
        const onDown = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, []);

    const getSections = () => Array.from(document.querySelectorAll('.exportable-section'));

    const sectionTitle = (section, i) =>
        section.dataset.exportTitle ||
        section.querySelector('h4, h3, h2')?.textContent?.trim() ||
        `chart-${i + 1}`;

    const collectCSV = () => {
        const blocks = [];
        getSections().forEach((section) => {
            let data = null;
            try { data = section.dataset.exportData ? JSON.parse(section.dataset.exportData) : null; } catch { data = null; }
            if (!data?.headers || !data?.rows) return;
            blocks.push(csvSection(data.title, data.headers, data.rows));
        });
        if (!blocks.length) return '';
        return [...metaLines('Circularity Dashboard', meta), '', blocks.join('\n\n')].join('\n');
    };

    const exportAllToCSV = async () => {
        cancelled.current = false;
        setExportType('csv');
        setExportStatus('preparing');
        setExportProgress(0);
        try {
            const csv = collectCSV();
            if (!csv) { setExportStatus('error'); return; }
            if (cancelled.current) return;
            setDownload({ csv, name: `circularity-dashboard-${fileDate()}.csv` });
            setExportProgress(100);
            setExportStatus('ready');
        } catch (err) {
            console.error('Error exporting CSV:', err);
            setExportStatus('error');
        }
    };

    const exportToZIP = async () => {
        cancelled.current = false;
        setExportType('zip');
        setExportStatus('preparing');
        setExportProgress(0);
        try {
            const zip = new JSZip();
            const sections = getSections();
            if (!sections.length) { setExportStatus('error'); return; }

            const used = new Set();
            let completed = 0;
            const batchSize = 3;

            for (let i = 0; i < sections.length; i += batchSize) {
                if (cancelled.current) return;
                const batch = sections.slice(i, i + batchSize);
                await Promise.all(batch.map(async (section, idx) => {
                    const title = sectionTitle(section, i + idx);
                    try {
                        // The shared helper leaves out anything with the
                        // "export-controls" class (the per-card Export button).
                        const dataUrl = await nodeToPngDataUrl(section);
                        const blob = await (await fetch(dataUrl)).blob();
                        let name = slug(title);
                        let n = 2;
                        while (used.has(name)) name = `${slug(title)}_${n++}`;
                        used.add(name);
                        zip.file(`${name}.png`, blob);
                    } catch (err) {
                        console.error(`Error exporting ${title}:`, err);
                    } finally {
                        completed++;
                        setExportProgress(Math.round((completed / sections.length) * 90));
                    }
                }));
            }

            const csv = collectCSV();
            if (csv) zip.file('circularity-dashboard-data.csv', '﻿' + csv);

            const content = await zip.generateAsync({ type: 'blob' });
            if (cancelled.current) return;
            setDownload({ blob: content, name: `circularity-dashboard-${fileDate()}.zip` });
            setExportProgress(100);
            setExportStatus('ready');
        } catch (err) {
            console.error('Error creating ZIP:', err);
            setExportStatus('error');
        }
    };

    const handleDownload = () => {
        if (!download) return;
        if (download.csv) downloadCSV(download.csv, download.name);
        else saveAs(download.blob, download.name);
        setDownload(null);
        setExportStatus(null);
    };

    const cancelExport = () => {
        cancelled.current = true;
        setExportStatus(null);
        setDownload(null);
    };

    return (
        <div className="dashboard-export-container relative">
            {exportStatus && (
                <div className="fixed bottom-4 left-4 z-50 bg-background p-4 rounded-lg shadow-lg border flex items-center gap-4">
                    {exportStatus === 'preparing' && (
                        <>
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                            <div>
                                <p className="font-medium">
                                    {t('circularityExport.preparing', 'Preparing export')} ({exportType?.toUpperCase()})
                                </p>
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                    <div className="bg-primary h-2 rounded-full" style={{ width: `${exportProgress}%` }}></div>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {exportProgress}% {t('circularityExport.complete', 'complete - you can keep using the dashboard')}
                                </p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={cancelExport}>
                                {t('circularityExport.cancel', 'Cancel')}
                            </Button>
                        </>
                    )}

                    {exportStatus === 'ready' && (
                        <>
                            <CheckCircle className="h-6 w-6 text-green-500" />
                            <div>
                                <p className="font-medium">{t('circularityExport.ready', 'Export ready')}</p>
                                <p className="text-xs text-muted-foreground">{download?.name}</p>
                            </div>
                            <Button onClick={handleDownload}>{t('circularityExport.download', 'Download')}</Button>
                            <Button variant="ghost" size="sm" onClick={cancelExport}>
                                {t('circularityExport.dismiss', 'Dismiss')}
                            </Button>
                        </>
                    )}

                    {exportStatus === 'error' && (
                        <>
                            <XCircle className="h-6 w-6 text-red-500" />
                            <div>
                                <p className="font-medium">{t('circularityExport.failed', 'Export failed')}</p>
                                <p className="text-xs text-muted-foreground">
                                    {t('circularityExport.failedHint', 'Wait for the charts to finish loading, then try again')}
                                </p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={cancelExport}>
                                {t('circularityExport.dismiss', 'Dismiss')}
                            </Button>
                        </>
                    )}
                </div>
            )}

            <div className="fixed bottom-4 right-4 z-40">
                <div className="relative">
                    <Button
                        ref={buttonRef}
                        onClick={() => setIsOpen(!isOpen)}
                        className="flex items-center gap-2"
                        disabled={exportStatus === 'preparing'}
                    >
                        <Download className="w-4 h-4" />
                        {t('circularityExport.exportData', 'Export Data')}
                    </Button>

                    {isOpen && (
                        <div
                            ref={dropdownRef}
                            className="absolute bottom-full right-0 mb-2 w-48 bg-background rounded-md shadow-lg border p-1 z-50"
                        >
                            <Button
                                variant="ghost"
                                className="w-full justify-start"
                                onClick={() => { setIsOpen(false); exportAllToCSV(); }}
                                disabled={exportStatus === 'preparing'}
                            >
                                {t('circularityExport.asCSV', 'Export as CSV')}
                            </Button>
                            <Button
                                variant="ghost"
                                className="w-full justify-start"
                                onClick={() => { setIsOpen(false); exportToZIP(); }}
                                disabled={exportStatus === 'preparing'}
                            >
                                {t('circularityExport.asPNGZip', 'Export as PNG (ZIP)')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {children}
        </div>
    );
}

export default CircularityExport;